// =============================================================================
// A time field for the event form.
//
// On a touch screen the native iOS wheel ignores the 5-minute `step`, so the
// field opens its own wheel: hours and minutes in five-minute steps, snap
// scrolling, a band marking the pick. The keyboard button beside the field
// switches to a plain text box for the odd 14:37. On a desktop the browser's
// own time input does honour the step, so it stays.
// =============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Clock } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';

const pad = (n) => String(n).padStart(2, '0');
const HOURS = Array.from({ length: 24 }, (_, i) => pad(i));
const MINUTES = Array.from({ length: 12 }, (_, i) => pad(i * 5));
const ROW = 40; // px per wheel row
const VISIBLE = 5;

export const normalizeTime = (raw) => {
  const m = /^\s*(\d{1,2})\s*[:.]?\s*(\d{2})\s*$/.exec(raw || '');
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${pad(h)}:${pad(min)}`;
};

// 'HH:MM' → the wheel's nearest position, e.g. 17:32 → 17 / 30.
export const wheelPosition = (value) => {
  const m = /^(\d{2}):(\d{2})$/.exec(value || '');
  if (!m) return { hour: '09', minute: '00' };
  const minute = Math.min(55, Math.round(Number(m[2]) / 5) * 5);
  return { hour: m[1], minute: pad(minute) };
};

const coarsePointer = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

// One snapping column. Scrolls to `value` when it mounts; reports the row
// under the band once the scroll has settled.
const WheelColumn = ({ items, value, onPick }) => {
  const ref = useRef(null);
  const settle = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = Math.max(0, items.indexOf(value)) * ROW;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onScroll = () => {
    clearTimeout(settle.current);
    settle.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const idx = Math.min(items.length - 1, Math.max(0, Math.round(el.scrollTop / ROW)));
      if (items[idx] !== value) onPick(items[idx]);
    }, 120);
  };
  const pad2 = ((VISIBLE - 1) / 2) * ROW;
  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className="relative h-[200px] w-20 overflow-y-auto no-scrollbar overscroll-contain"
      style={{ scrollSnapType: 'y mandatory', paddingTop: pad2, paddingBottom: pad2 }}
    >
      {items.map((it) => (
        <button
          type="button"
          key={it}
          onClick={() => {
            ref.current?.scrollTo({ top: items.indexOf(it) * ROW, behavior: 'smooth' });
            onPick(it);
          }}
          className={`w-full flex items-center justify-center text-2xl tabular-nums ${
            it === value ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
          }`}
          style={{ height: ROW, scrollSnapAlign: 'center' }}
        >
          {it}
        </button>
      ))}
    </div>
  );
};

const TimeWheel = ({ value, onChange, onClose, onType }) => {
  const { t } = useTranslation();
  const start = wheelPosition(value);
  const [hour, setHour] = useState(start.hour);
  const [minute, setMinute] = useState(start.minute);
  const pick = (h, m) => {
    setHour(h);
    setMinute(m);
    onChange(`${h}:${m}`);
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-end justify-center" onClick={onClose}>
      <div
        className="w-full sm:max-w-sm bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl sm:mb-8 shadow-2xl px-4 pt-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onType}
            className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label={t('calendar.typeTime')}
            title={t('calendar.typeTime')}
          >
            <Keyboard size={18} />
          </button>
          <div className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
            {hour}:{minute}
          </div>
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm font-semibold text-primary-600">
            {t('common.done', 'Fertig')}
          </button>
        </div>
        <div className="relative mt-2 flex items-center justify-center gap-2">
          <div
            className="absolute left-4 right-4 rounded-xl bg-gray-100 dark:bg-gray-800 pointer-events-none"
            style={{ top: ((VISIBLE - 1) / 2) * ROW, height: ROW }}
            aria-hidden="true"
          />
          <WheelColumn items={HOURS} value={hour} onPick={(h) => pick(h, minute)} />
          <div className="text-2xl font-semibold text-gray-400 relative">:</div>
          <WheelColumn items={MINUTES} value={minute} onPick={(m) => pick(hour, m)} />
        </div>
      </div>
    </div>
  );
};

const TimeField = ({ value, onChange, className = '', ariaLabel }) => {
  const { t } = useTranslation();
  const [typing, setTyping] = useState(false);
  const [wheel, setWheel] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);
  const touch = coarsePointer();

  useEffect(() => {
    if (typing) {
      setDraft(value || '');
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [typing, value]);

  const commit = () => {
    const next = normalizeTime(draft);
    if (next && next !== value) onChange(next);
    setTyping(false);
  };

  return (
    <div className="flex items-center gap-1 min-w-0">
      {typing ? (
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          placeholder="14:37"
          className={className}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              setTyping(false);
            }
          }}
          aria-label={ariaLabel}
        />
      ) : touch ? (
        <button
          type="button"
          onClick={() => setWheel(true)}
          className={`${className} text-left tabular-nums`}
          aria-label={ariaLabel}
        >
          {value || '--:--'}
        </button>
      ) : (
        <input
          type="time"
          step="300"
          className={className}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          aria-label={ariaLabel}
        />
      )}
      <button
        type="button"
        onClick={() => setTyping((v) => !v)}
        className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
        title={typing ? t('calendar.pickTime') : t('calendar.typeTime')}
        aria-label={typing ? t('calendar.pickTime') : t('calendar.typeTime')}
      >
        {typing ? <Clock size={16} /> : <Keyboard size={16} />}
      </button>
      {wheel && (
        <TimeWheel
          value={value}
          onChange={onChange}
          onClose={() => setWheel(false)}
          onType={() => {
            setWheel(false);
            setTyping(true);
          }}
        />
      )}
    </div>
  );
};

export default TimeField;
